import { Box, Typography } from "@mui/material";

const ACCENT_RED = "#bf2120";
const LETTER_SPACING = "0.1em";

/**
 * Figma: Rectangle 43(黒板) + Rectangle 42(内側の強いグロー) を重ねた発光プレート。
 * - 幅   : トラッキング分を除いた「文字1つ分」の字送り幅にぴったり合わせる
 * - 高さ : 1em（キャップハイトの上下へ約 0.125em ずつはみ出す）
 * - 位置 : ベースラインの 0.847em 上。line-height:1 の inline-block は
 *          上端からベースラインまでが 0.933em なので、上端からの差分は 0.086em。
 */
const GLOW_PLATE = {
  position: "absolute",
  left: 0,
  right: LETTER_SPACING,
  top: "0.086em",
  height: "1em",
  backgroundColor: "#000000",
  boxShadow: "0 0 4px rgba(255, 255, 255, 0.13), 0 0 10px rgba(255, 255, 255, 0.22)",
};

/**
 * omagun のセクション見出し（Figma: "Story" / "Epilogue" / "Ticket" / "Others"）。
 * Noto Serif JP 400 / 36px / letter-spacing 0.1em。
 * line-height は Figma 上でセクションごとに異なるため呼び出し側から渡す。
 *
 * 文字単位の装飾を index で指定する:
 * - accentIndex: アクセントレッドにする文字。null で無効。
 * - plateIndex : 背後に黒い発光プレートを敷く文字。null で無効。
 */
export default function OmagunSectionHeading({
  label,
  accentIndex = 1,
  plateIndex = 0,
  lineHeight = 40,
  sx = {},
}) {
  // 装飾しない文字は連結したままにして、不要な span 分割で字面が崩れないようにする。
  const segments = [...label].reduce((acc, char, index) => {
    const decorated = index === accentIndex || index === plateIndex;
    const last = acc[acc.length - 1];
    if (!decorated && last && !last.decorated) {
      last.text += char;
      return acc;
    }
    acc.push({ decorated, text: char, index });
    return acc;
  }, []);

  return (
    <Typography
      component="h2"
      // 装飾で文字を span に割るとアクセシブル名が "S t ory" のように分断されるため明示する。
      aria-label={label}
      sx={{
        fontFamily: '"Noto Serif JP", serif',
        fontWeight: 400,
        fontSize: "36px",
        lineHeight: `${lineHeight}px`,
        letterSpacing: LETTER_SPACING,
        textAlign: "center",
        color: "#fff",
        ...sx,
      }}
    >
      {segments.map(({ decorated, text, index }) => {
        if (!decorated) return <span key={index}>{text}</span>;

        const color = index === accentIndex ? ACCENT_RED : "inherit";
        if (index !== plateIndex) {
          return <Box component="span" key={index} sx={{ color }}>{text}</Box>;
        }
        return (
          <Box component="span" key={index} sx={{ position: "relative", display: "inline-block", lineHeight: 1 }}>
            <Box component="span" aria-hidden data-testid="glow-plate" sx={GLOW_PLATE} />
            <Box component="span" sx={{ position: "relative", color }}>{text}</Box>
          </Box>
        );
      })}
    </Typography>
  );
}
